import {useCallback, useRef} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";

import {API} from "api";
import {useSetting} from "contexts/setting";
import {ALL_PRODUCT} from "data/constants";
import {useCurrentSet} from "hooks/useCurrentSet";
import {
  getChannelSettingsQueryKey,
  useChannelSettings,
  type ChannelSettings,
} from "hooks/useChannelSettings";
import {setBackgroundOrderKey, setBackgroundOrderOf} from "libs/selectorVisibility";

/**
 * The background tile order of the page's releases set
 * (`setBackgroundOrders`, libs/selectorVisibility.ts).
 *
 * A set with no order of its own shows the product's order. A set with one
 * puts its own first: the tiles, and the background a page without
 * `?background=` opens on (default-scenes-resolved.ts).
 */
export interface SetBackgroundOrder {
  /** The page has a releases set whose gallery can have its own order. */
  isAvailable: boolean;
  /** The set's own order, or null when the set uses the product's order. */
  order: number[] | null;
  /** The page's set value, or undefined off releases. */
  set: string | undefined;
}

interface Scope {
  dataChannel: string;
  dataType: string;
  product: {value: string};
}

/**
 * The set's order as the page reads it. Off releases, on the ALL-products
 * view, and with a server that does not send `setBackgroundOrders`, there is
 * none.
 */
export function useSetBackgroundOrderOf(): SetBackgroundOrder {
  const {dataChannel, dataType, product} = useSetting() as unknown as Scope;
  const set: string | undefined = useCurrentSet();
  const {data: settings} = useChannelSettings(dataChannel);
  const isAvailable =
    dataChannel === "releases" &&
    Boolean(set) &&
    product?.value !== ALL_PRODUCT.value &&
    settings?.setBackgroundOrders !== undefined;
  return {
    isAvailable,
    order: isAvailable ? setBackgroundOrderOf(settings, dataType, product.value, set) : null,
    set,
  };
}

/**
 * The set's order and its write (superadmin). A save shows at once, and the
 * writes go to the server one at a time in the order they were made: the last
 * drag is the order that stays. An empty order gives the set the product's
 * order again.
 */
export function useSetBackgroundOrder(): SetBackgroundOrder & {
  save: (order: number[]) => void;
} {
  const offer = useSetBackgroundOrderOf();
  const {dataChannel, dataType, product} = useSetting() as unknown as Scope;
  const queryClient = useQueryClient();
  const pending = useRef(0);

  const {mutate} = useMutation({
    // One queue for every set order: a later write never overtakes an earlier one.
    scope: {id: "set-background-order"},
    mutationFn: (input: {type: string; product: string; set: string; order: number[]}) =>
      API.putSetBackgroundOrder({channel: dataChannel, ...input}),
    onMutate: async (input) => {
      pending.current += 1;
      const queryKey = getChannelSettingsQueryKey(dataChannel);
      await queryClient.cancelQueries({queryKey});
      queryClient.setQueryData<ChannelSettings>(queryKey, (current) => {
        if (!current) return current;
        const key = setBackgroundOrderKey(input.type, input.product, input.set);
        const {[key]: _previous, ...rest} = current.setBackgroundOrders ?? {};
        return {
          ...current,
          setBackgroundOrders: input.order.length > 0 ? {...rest, [key]: input.order} : rest,
        };
      });
    },
    onError: (err) => {
      console.error("[useSetBackgroundOrder] Save failed:", err);
    },
    onSettled: () => {
      // Read the server's settings back when no other write is queued: an
      // earlier answer must not undo a later drag on the screen.
      pending.current -= 1;
      if (pending.current > 0) return undefined;
      return queryClient.invalidateQueries({queryKey: getChannelSettingsQueryKey(dataChannel)});
    },
  });

  const save = useCallback(
    (order: number[]) => {
      if (!offer.isAvailable || !offer.set) {
        throw new Error("This page has no releases set with its own background order");
      }
      mutate({type: dataType, product: product.value, set: offer.set, order});
    },
    [offer.isAvailable, offer.set, mutate, dataType, product.value]
  );

  return {...offer, save};
}
